import { slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../Button/index.js';
import { i18n } from '../../../i18n/translate.js';
import { SeeAllFilterButton } from '../SeeAllFilterButton.js';
import { SearchComponent } from '../../Form/SearchComponent.js';
import { useBreakpointRef } from '../../../hooks/useBreakpointRef.js';
import { useContainerProps } from '../../../hooks/useContainerProps.js';
import { useEffectDeep } from '../../../hooks/useEffectDeep.js';
import { DateFilter } from '../DateFilter.js';
import { FilterModal } from '../../FilterModal/index.js';
import { SelectFilter } from '../SelectFilter.js';
import { FilterChips } from '../FilterChips/index.js';
import { getInitialFilters, isFilterActive, filtersArrayToKeyValueObject, removeFilterFromCollection, clearAllFilters } from '../functions/index.js';
import { noop } from '../../../utils/noop.js';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import { Layout } from '../../../core/components/Layout/component.js';
import { ButtonVariant } from '../../../core/components/Button/types.js';
var BREAKPOINTS = Object.freeze([576, 768, 1080, 1280, 1480, 1788]);
var BREAKPOINT_VALUES = Object.freeze([{
  numberOfFilter: 1,
  chipsStyle: {
    marginTop: '4px'
  },
  searchFullWidth: true,
  searchStyle: {
    'flex-basis': '100%',
    'flex-wrap': 'wrap-reverse',
    marginBottom: '24px'
  }
}, {
  numberOfFilter: 1,
  chipsStyle: {
    marginTop: '4px'
  }
}, {
  numberOfFilter: 1,
  chipsStyle: {
    marginTop: '8px'
  }
}, {
  numberOfFilter: 2,
  chipsStyle: {
    marginTop: '8px'
  }
}, {
  numberOfFilter: 3,
  chipsStyle: {
    marginTop: '8px'
  }
}, {
  numberOfFilter: 4,
  chipsStyle: {
    marginTop: '8px'
  }
}, {
  numberOfFilter: 5,
  chipsStyle: {
    marginTop: '8px'
  }
}]);

var FilterBar = function FilterBar(props) {
  props = useContainerProps(props);

  var _useBreakpointRef = useBreakpointRef({
    breakpoints: BREAKPOINTS,
    breakpointValues: BREAKPOINT_VALUES,
    direction: ''
  }),
      breakpointValue = _useBreakpointRef.breakpointValue;

  var _props = props,
      _props$filterConfig = _props.filterConfig,
      filterConfig = _props$filterConfig === void 0 ? [] : _props$filterConfig,
      filterModalProps = _props.filterModalProps,
      _props$initialFilters = _props.initialFilters,
      initialFilters = _props$initialFilters === void 0 ? {} : _props$initialFilters,
      _props$onSelectFilter = _props.onSelectFilters,
      onSelectFilters = _props$onSelectFilter === void 0 ? noop : _props$onSelectFilter,
      _props$onClearFilters = _props.onClearFilters,
      onClearFilters = _props$onClearFilters === void 0 ? noop : _props$onClearFilters,
      searchBox = _props.searchBox,
      sortSelect = _props.sortSelect;

  var _useState = useState(getInitialFilters(filterConfig, initialFilters)),
      _useState2 = _slicedToArray(_useState, 2),
      filters = _useState2[0],
      setFilters = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showFilterModal = _useState4[0],
      setShowFilterModal = _useState4[1];

  var activeFilters = filters.filter(function (filter) {
    return isFilterActive(filter);
  });
  var showingPartialFilters = !!(breakpointValue && filters.length > breakpointValue.numberOfFilter);
  var childRef = useRef(null);
  useEffectDeep(function () {
    var newFilters = getInitialFilters(filterConfig, initialFilters); // only set filters from config if something changed dynamically

    if (!isEqual(filters, newFilters)) {
      setFilters(newFilters);
    }
  }, [filterConfig]);
  useEffect(function () {
    // execute passed onChange every time filters are updated
    onSelectFilters(filtersArrayToKeyValueObject(activeFilters));
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  var clearFilters = function clearFilters() {
    var newFilters = clearAllFilters(filters);
    setFilters(newFilters);
    onClearFilters();
  };

  var removeFilter = function removeFilter(filterToRemove) {
    var newFilters = removeFilterFromCollection(filterToRemove, filters);
    setFilters(newFilters);
  };

  var handleFilterChange = function handleFilterChange(updatedFilter, value) {
    var newFilters = filters.map(function (filter) {
      if (filter.name !== updatedFilter.name) {
        return filter;
      }

      if (filter.type !== DateFilter) {
        filter.value = value;
        return filter;
      }

      filter.value = value ? _objectSpread2(_objectSpread2({}, filter.value), value) : null;

      if (filter.value && Object.values(filter.value).every(function (val) {
        return val === null;
      })) {
        filter.value = null;
      }

      return filter;
    });
    setFilters(newFilters);
  };

  var showAllFilters = function showAllFilters() {
    return setShowFilterModal(true);
  };

  var onModalApplyFilters = function onModalApplyFilters(updatedFilters) {
    if (!Object.keys(updatedFilters).length) {
      // empty object means all filters were removed
      clearFilters();
    } else {
      // update filter values
      var newFilters = _toConsumableArray(filters);

      Object.entries(updatedFilters).forEach(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
            name = _ref2[0],
            value = _ref2[1];

        newFilters = newFilters.map(function (filter) {
          if (filter.name === name) {
            filter.value = value;
          }

          return filter;
        });
      });
      setFilters(newFilters);
    }

    setShowFilterModal(false);
  };

  var renderFiltersAndSearch = function renderFiltersAndSearch() {
    var breakpointUXAdjustment = 1; // to make adjustment as seeAllButton is considered as a filter pill as per the design

    var maxDisplayedFilters = breakpointValue && showingPartialFilters ? breakpointValue.numberOfFilter - breakpointUXAdjustment : filters.length;
    var visibleFilters = filters.slice(0, maxDisplayedFilters);
    return /*#__PURE__*/React.createElement(Layout, {
      variant: "flex",
      flexDirection: 'row',
      flexWrap: "wrap",
      justifyContent: 'space-between'
    }, /*#__PURE__*/React.createElement(Layout, {
      variant: "flex",
      flexDirection: 'row',
      gap: 2,
      "data-object-type": "filters"
    }, visibleFilters.map(function (filter) {
      var FilterComponent = filter.type;
      return /*#__PURE__*/React.createElement(FilterComponent, _extends({}, filter, {
        ref: childRef,
        key: filter.name,
        label: filter.label,
        onChange: function onChange(value) {
          return handleFilterChange(filter, value);
        },
        disabled: props.disabled || !!filter.disabled,
        active: isFilterActive(filter),
        clearable: true
      }));
    }), showingPartialFilters && /*#__PURE__*/React.createElement(SeeAllFilterButton, {
      onClick: showAllFilters,
      disabled: props.disabled
    }), activeFilters.length > 0 && /*#__PURE__*/React.createElement(Button, {
      onClick: clearFilters,
      style: ButtonVariant.LINK,
      "data-action": "clear-filters",
      icon: "xmark-circle",
      label: i18n.t('Clear Filters'),
      disabled: props.disabled
    })), renderSearch(), renderSortSelect());
  };

  var renderSortSelect = function renderSortSelect() {
    var _sortSelect$data, _sortSelect$data2, _sortSelect$data3;

    if (!sortSelect) return null;
    var sortSelectDataAttributes = {};

    if (sortSelect !== null && sortSelect !== void 0 && (_sortSelect$data = sortSelect.data) !== null && _sortSelect$data !== void 0 && _sortSelect$data.action) {
      sortSelectDataAttributes['data-action'] = sortSelect.data.action;
    }

    if (sortSelect !== null && sortSelect !== void 0 && (_sortSelect$data2 = sortSelect.data) !== null && _sortSelect$data2 !== void 0 && _sortSelect$data2.id) {
      sortSelectDataAttributes['data-object-id'] = sortSelect.data.id;
    }

    if (sortSelect !== null && sortSelect !== void 0 && (_sortSelect$data3 = sortSelect.data) !== null && _sortSelect$data3 !== void 0 && _sortSelect$data3.type) {
      sortSelectDataAttributes['data-object-type'] = sortSelect.data.type;
    }

    return /*#__PURE__*/React.createElement("div", _extends({}, sortSelectDataAttributes, {
      className: "sort",
      style: breakpointValue && breakpointValue.searchStyle
    }), /*#__PURE__*/React.createElement("span", {
      className: "m-r-10"
    }, i18n.t('Sort By'), ":"), /*#__PURE__*/React.createElement(SelectFilter, {
      defaultValue: sortSelect.defaultValue,
      onChange: sortSelect.sortAction,
      options: sortSelect.options,
      selectedKey: sortSelect.selectedKey,
      showSelected: true
    }));
  };

  var renderSearch = function renderSearch() {
    var _searchBox$data, _searchBox$data2, _searchBox$data3;

    if (!searchBox) return null;
    var searchBoxDataAttributes = {};

    if (searchBox !== null && searchBox !== void 0 && (_searchBox$data = searchBox.data) !== null && _searchBox$data !== void 0 && _searchBox$data.action) {
      searchBoxDataAttributes['data-action'] = searchBox.data.action;
    }

    if (searchBox !== null && searchBox !== void 0 && (_searchBox$data2 = searchBox.data) !== null && _searchBox$data2 !== void 0 && _searchBox$data2.id) {
      searchBoxDataAttributes['data-object-id'] = searchBox.data.id;
    }

    if (searchBox !== null && searchBox !== void 0 && (_searchBox$data3 = searchBox.data) !== null && _searchBox$data3 !== void 0 && _searchBox$data3.type) {
      searchBoxDataAttributes['data-object-type'] = searchBox.data.type;
    }

    return /*#__PURE__*/React.createElement("div", _extends({}, searchBoxDataAttributes, {
      className: "search",
      style: breakpointValue && breakpointValue.searchStyle
    }), /*#__PURE__*/React.createElement(SearchComponent, {
      searchAction: searchBox.searchAction,
      placeholder: searchBox.placeholder || i18n.t('Search'),
      valueChangeDelay: searchBox.valueChangeDelay,
      wrapperClass: breakpointValue && breakpointValue.searchFullWidth ? 'full-width' : undefined,
      value: searchBox.value
    }));
  };

  var renderFilterModal = function renderFilterModal() {
    if (!showFilterModal) return null;
    return /*#__PURE__*/React.createElement(FilterModal, {
      backdropClassName: filterModalProps === null || filterModalProps === void 0 ? void 0 : filterModalProps.backdropClassName,
      className: filterModalProps === null || filterModalProps === void 0 ? void 0 : filterModalProps.className,
      filterConfig: filters.map(function (filter) {
        return omit(filter, 'value');
      }),
      initialFilters: filtersArrayToKeyValueObject(activeFilters),
      onSelectFilters: onModalApplyFilters,
      onClose: function onClose() {
        return setShowFilterModal(false);
      },
      show: true,
      title: i18n.t('Filters')
    });
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, renderFiltersAndSearch(), /*#__PURE__*/React.createElement(FilterChips, {
    filters: activeFilters,
    onRemoveFilter: removeFilter,
    disabled: props.disabled
  }), renderFilterModal());
};

export { FilterBar };